import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Keeps a chat thread pinned to the newest message — but only while the agent
 * is already there.
 *
 * The thread polls every few seconds, and an unconditional scroll-to-bottom on
 * every refresh yanks the view back down mid-read, making older messages
 * impossible to review. Once the agent scrolls up we stop following and surface
 * a jump-to-latest affordance instead, resuming only when they return to the
 * bottom themselves.
 */
const AT_BOTTOM_SLACK_PX = 80

export function useStickyScroll<T>(dep: T) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [atBottom, setAtBottom] = useState(true)
  const [unseen, setUnseen] = useState(0)
  const atBottomRef = useRef(true)
  atBottomRef.current = atBottom

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    const el = containerRef.current
    if (!el) return
    el.scrollTo({ top: el.scrollHeight, behavior })
    setUnseen(0)
  }, [])

  const handleScroll = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight
    const nowAtBottom = distance <= AT_BOTTOM_SLACK_PX
    setAtBottom(nowAtBottom)
    if (nowAtBottom) setUnseen(0)
  }, [])

  // Follow new content only when the agent hasn't scrolled away.
  useEffect(() => {
    if (atBottomRef.current) {
      // 'auto' — a smooth animation on every poll tick reads as jitter.
      scrollToBottom('auto')
    } else {
      setUnseen((n) => n + 1)
    }
    // Intentionally keyed on `dep` alone: this must fire when the thread's
    // content changes, not when the scroll callback identity does.
  }, [dep, scrollToBottom])

  return { containerRef, atBottom, unseen, scrollToBottom, handleScroll }
}
